'use client';

/**
 * StatusFilterSelect Component
 *
 * A select component optimized for filtering by status values.
 * Includes an "All" option that clears the filter.
 * Built on MagicSelect for consistent styling.
 */

import type { SelectChangeEvent } from '@mui/material/Select';
import { forwardRef, useCallback } from 'react';
import { MagicSelect, type MagicSelectOption, type MagicSelectProps } from '../MagicSelect';

// ============================================================================
// Types
// ============================================================================

export interface StatusFilterOption {
  /** The filter value (empty string means "All") */
  value: string;
  /** Display label */
  label: string;
  /** Optional disabled state */
  disabled?: boolean;
}

export interface StatusFilterSelectProps extends Omit<MagicSelectProps, 'options' | 'value' | 'onChange'> {
  /**
   * Status options to display (excluding "All")
   * The "All" option is automatically prepended
   */
  options: StatusFilterOption[];

  /**
   * Currently selected status value
   * Empty string or undefined means "All"
   */
  value?: string;

  /**
   * Callback when selection changes
   * Returns undefined when "All" is selected
   */
  onChange?: (value: string | undefined) => void;

  /**
   * Label for the "All" option
   * @default 'All'
   */
  allLabel?: string;

  /**
   * Placeholder for empty value display
   * @default 'All'
   */
  placeholder?: string;
}

// ============================================================================
// Component
// ============================================================================

export const StatusFilterSelect = forwardRef<HTMLDivElement, StatusFilterSelectProps>(
  ({ options, value, onChange, allLabel = 'All', placeholder = 'All', label, ...props }, ref) => {
    // Build options with "All" prepended
    const selectOptions: MagicSelectOption[] = [
      { value: '', label: allLabel },
      ...options.map((opt) => ({
        value: opt.value,
        label: opt.label,
        disabled: opt.disabled,
      })),
    ];

    // Handle change event
    const handleChange = useCallback(
      (event: SelectChangeEvent<unknown>) => {
        const newValue = event.target.value as string;
        // Convert empty string to undefined for cleaner API
        onChange?.(newValue === '' ? undefined : newValue);
      },
      [onChange],
    );

    return (
      <MagicSelect
        ref={ref}
        label={label}
        options={selectOptions}
        value={value ?? ''}
        onChange={handleChange}
        displayEmpty
        {...props}
      />
    );
  },
);

StatusFilterSelect.displayName = 'StatusFilterSelect';

export default StatusFilterSelect;
